<template>
  <component :is="tag" class="Navigation" :class="modifierClass">
    <template v-for="item of items" :key="item.id">
      <slot name="item" :item :buttonProps="buttonProps(item)">
        <li class="Navigation-item">
          <ActionButton
            v-bind="{ ...buttonProps(item), ...linkComponentProps(item) }"
            :tag="item.href ? linkComponent : 'button'"
            :href="item.href"
            :target="item.target"
            :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined"
            :aria-current="item.id === activeId ? 'page' : undefined"
            @click="item.action?.()"
          >
            {{ item.label }}
          </ActionButton>
        </li>
      </slot>
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import type { NavigationButtonProps, NavigationItem, NavigationProps, NavigationSlots } from './Navigation.model';

const {
  activeId,
  size = 'md',
  activeStyle = 'highlight',
  tag = 'ul',
  linkComponent = 'a',
} = defineProps<NavigationProps>();

defineSlots<NavigationSlots>();

const modifierClass = computed(() => ({ 'Navigation--textActive': activeStyle === 'text' }));

function buttonProps(item: NavigationItem): NavigationButtonProps {
  return {
    variant: 'list',
    size,
    leftIcon: item.icon,
    active: item.id === activeId,
    class: 'Navigation-button',
  };
}

/** `external` is a prop of link components such as NuxtLink, not an attribute of a plain anchor. */
function linkComponentProps(item: NavigationItem) {
  return linkComponent !== 'a' && item.external !== undefined ? { external: item.external } : {};
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .Navigation {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-1;
    margin: 0;
    padding: 0;
    list-style: none;

    &--textActive {
      --button-active-bg: transparent;
      --button-active-shadow: none;
    }

    &-button {
      width: 100%;
    }
  }
}
</style>
